/**
 * Checking that a snapshot came from the server and has not been edited since.
 *
 * The order here is the whole design. The bytes are hashed, the hash is checked
 * against the signature, and only then is anything parsed. A verifier that
 * parsed first and re-serialised to check the signature would be asking Ruby and
 * JavaScript to agree forever on key order, number formatting and string
 * escaping — and the day they disagreed, every device would refuse every
 * snapshot, with nothing in the error to say why.
 *
 * So `payload` travels as a string and stays one. `JSON.parse` happens after
 * `verifySnapshot` returns true, never before.
 *
 * The canonical string is pinned byte-for-byte against
 * `Authorization::SnapshotSigner.canonical_string`, with one shared vector in
 * both test suites — the same discipline `device/signing.ts` follows.
 */
import { importVerificationKey, verifySignature } from "../../device/keypair"
import { sha256Hex } from "../../device/signing"

export interface SnapshotEnvelope {
  stationUuid: string
  version: number
  payload: string
  digest: string
  signature: string
  publicKey: string
  issuedAt: string
  expiresAt: string
}

export type SnapshotRejection = "digest_mismatch" | "signature_invalid" | "malformed"

export interface SnapshotVerification {
  valid: boolean
  rejection?: SnapshotRejection
}

export function canonicalSnapshotString(envelope: Pick<SnapshotEnvelope, "stationUuid" | "version" | "digest" | "issuedAt" | "expiresAt">): string {
  return [
    envelope.stationUuid,
    String(envelope.version),
    envelope.digest,
    envelope.issuedAt,
    envelope.expiresAt
  ].join("\n")
}

export async function verifySnapshot(envelope: SnapshotEnvelope): Promise<SnapshotVerification> {
  if (!envelope.payload || !envelope.digest || !envelope.signature || !envelope.publicKey) {
    return { valid: false, rejection: "malformed" }
  }

  // The digest first, because it is the cheap half and because a payload that
  // does not match its own digest is a different failure from one whose
  // signature does not check out — the first is corruption, the second is
  // forgery, and an operator report of either should not be turned into the
  // other.
  if ((await sha256Hex(envelope.payload)) !== envelope.digest) {
    return { valid: false, rejection: "digest_mismatch" }
  }

  try {
    const key = await importVerificationKey(envelope.publicKey)
    const verified = await verifySignature(key, envelope.signature, canonicalSnapshotString(envelope))

    return verified ? { valid: true } : { valid: false, rejection: "signature_invalid" }
  } catch {
    return { valid: false, rejection: "malformed" }
  }
}

export interface SnapshotPayload {
  stationUuid: string
  stationCode: string
  stationName: string
  registers: Array<{ uuid: string; code: string; name: string }>
  users: Array<{
    uuid: string
    name: string
    initials: string
    active: boolean
    roles: Array<{ uuid: string; title: string }>
  }>
  accessMatrix: Array<{ userUuid: string; registerCode: string; accessLevel: AccessLevel }>
  ssIcRoleUuids: string[]
  sectionTiRoleUuids: string[]
  eligibleSuccessorUuids: string[]
}

export type AccessLevel = "read_only" | "write" | "admin"

/** Only ever called on an envelope `verifySnapshot` has already accepted. */
export function parseSnapshotPayload(envelope: SnapshotEnvelope): SnapshotPayload {
  return JSON.parse(envelope.payload) as SnapshotPayload
}
